<template>
  <div class="ra-bna__thumbnails-wrapper">
    <div class="ra-bna__thumbnails" role="group" :aria-label="thumbnailsWrapperLabel">
      <button
        v-if="hasScrollableThumbnails"
        class="ra-bna__thumbnails-btn ra-bna__thumbnails-btn-prev"
        type="button"
        :aria-label="thumbnailNavLabelPrev"
        @click="scrollLeft"
      >
        <ba-thumbnail-arrow />
      </button>
      <ul
        ref="thumbnailsList"
        class="ra-bna__thumbnails-list"
        @scrollend="updateVisibleThumbnailIndexes"
      >
        <li
          v-for="(image, index) in slides"
          :key="index"
          :class="[
            'ra-bna__thumbnails-item',
            { 'ra-bna__thumbnails-item--active': activeIndex === index },
          ]"
          :aria-hidden="!visibleThumbnailIndexes.has(index) || undefined"
        >
          <button
            type="button"
            class="ra-bna__thumbnails-item-icon"
            :aria-label="`${thumbnailsAccessibleLabel.replace('{{value}}', index + 1)} ${slides.length}`"
            :aria-current="activeIndex === index ? 'true' : undefined"
            :aria-controls="`${taggingId}-comparison-${index}`"
            :data-refapp-tagging-target="`thumbnail-${index}`"
            :tabindex="!visibleThumbnailIndexes.has(index) ? '-1' : undefined"
            @click="select(index)"
          >
            <img
              :src="image.thumbnail"
              :alt="image.beforeImage.altText"
              class="ra-bna__thumbnails-image"
            />
          </button>
        </li>
      </ul>
      <button
        v-if="hasScrollableThumbnails"
        class="ra-bna__thumbnails-btn ra-bna__thumbnails-btn-next"
        type="button"
        :aria-label="thumbnailNavLabelNext"
        @click="scrollRight"
      >
        <ba-thumbnail-arrow />
      </button>
    </div>
  </div>
</template>

<script>
import { debounce } from '../../../../shared/utils/debounce';
import { clamp } from '../../../../shared/utils/math';
import BaThumbnailArrow from '../icons/thumbnail-arrow.vue';

export default {
  name: 'refapp-beforeafter-thumbnails',
  components: { BaThumbnailArrow },
  inject: [
    'taggingId',
    'slides',
    'thumbnailNavLabelNext',
    'thumbnailNavLabelPrev',
    'thumbnailsWrapperLabel',
    'thumbnailsAccessibleLabel',
  ],
  props: {
    activeIndex: { type: Number, required: true },
  },
  emits: ['updateActiveLayout'],
  data() {
    return {
      visibleThumbnailIndexes: new Set(),
      hasScrollableThumbnails: false,
    };
  },
  mounted() {
    this.syncThumbnailsLayout();
    this.debouncedSyncThumbnailsLayout = debounce(this.syncThumbnailsLayout, 200);
    window.addEventListener('resize', this.debouncedSyncThumbnailsLayout);
  },
  beforeDestroy() {
    this.unbindResizeHandler();
  },
  beforeUnmount() {
    this.unbindResizeHandler();
  },
  methods: {
    unbindResizeHandler() {
      this.debouncedSyncThumbnailsLayout?.cancel();
      window.removeEventListener('resize', this.debouncedSyncThumbnailsLayout);
    },
    select(index) {
      this.$emit('updateActiveLayout', index);
      if (!this.visibleThumbnailIndexes.size) {
        return;
      }
      const firstVisible = Math.min(...this.visibleThumbnailIndexes);
      const lastVisible = Math.max(...this.visibleThumbnailIndexes);
      if (index === lastVisible) {
        this.scrollThumbnailIntoView(index + 1, 'end');
      } else if (index === firstVisible) {
        this.scrollThumbnailIntoView(index - 1);
      }
    },
    scrollLeft() {
      if (!this.visibleThumbnailIndexes.size) {
        return;
      }
      this.scrollThumbnailIntoView(Math.min(...this.visibleThumbnailIndexes) - 1);
    },
    scrollRight() {
      if (!this.visibleThumbnailIndexes.size) {
        return;
      }
      this.scrollThumbnailIntoView(Math.max(...this.visibleThumbnailIndexes) + 1, 'end');
    },
    scrollThumbnailIntoView(index, inline = 'start') {
      const items = this.$refs.thumbnailsList?.querySelectorAll('.ra-bna__thumbnails-item');
      if (!items?.length) {
        return;
      }
      const clampedIndex = clamp(index, 0, items.length - 1);
      items[clampedIndex]?.scrollIntoView?.({
        inline,
        block: 'nearest',
        behavior: 'smooth',
      });
    },
    syncThumbnailsLayout() {
      this.updateHasScrollableThumbnails();
      this.updateVisibleThumbnailIndexes();
    },
    updateHasScrollableThumbnails() {
      const list = this.$refs.thumbnailsList;
      this.hasScrollableThumbnails = Boolean(list && list.scrollWidth > list.clientWidth);
    },
    updateVisibleThumbnailIndexes() {
      const list = this.$refs.thumbnailsList;
      if (!list) {
        this.visibleThumbnailIndexes = new Set();
        return;
      }
      const listRect = list.getBoundingClientRect();
      const thumbnails = list.querySelectorAll('.ra-bna__thumbnails-item');
      const visibleThumbnailIndexes = new Set();

      thumbnails.forEach((item, index) => {
        const itemRect = item.getBoundingClientRect();
        const visibleWidth =
          Math.min(itemRect.right, listRect.right) - Math.max(itemRect.left, listRect.left);
        if (visibleWidth > 0) {
          visibleThumbnailIndexes.add(index);
        }
      });

      this.visibleThumbnailIndexes = visibleThumbnailIndexes;
    },
  },
};
</script>
